import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { useUser } from 'cor-business'
import styles from './index.module.scss'
import SearchIcon from '../../assets/icon/SearchIcon'
import CheckIcon from '../../assets/icon/CheckIcon'
import DotsVerticalIcon from '../../assets/icon/DotsVerticalIcon'
import PendingIcon from '../../assets/icon/PendingIcon'
import ResendIcon from '../../assets/icon/ResendIcon'
import CancelSendIcon from '../../assets/icon/CancelSendIcon'
import TrashIcon from '../../assets/icon/TrashIcon'
import EditIcon from '../../assets/icon/EditIcon'
import DropDown from '../../components/DropDown'
import Modal from '../../components/Modal'
import AddMemberModal from '../../components/AddMemberModal'
import Header from '../../components/Header'
import CloseIcon from '../../assets/icon/CloseIcon'
import WorkspaceMembersService from '../../services/workspace-members.service'
import ProjectService from '../../services/project.service'
import { useWorkspace } from '../../context/workspace.context'
import { extractList } from '../../utils/members'
import { canChangeMemberRole } from '../../utils/rolePermissions'
import {
    filterParticipants,
    getParticipantSearchLabel,
    matchesParticipantId,
} from './participantSearch'
import Pagination from '../../components/Pagination'
import { useListPagination } from '../../hooks/useListPagination'

interface ParticipantProject {
    id: number
    name: string
    projectMemberId?: number
}

interface Participant {
    id: number | string
    name: string
    email: string
    role: string
    status: string
    pending: boolean
    invitationId?: string | number | null
    projects: ParticipantProject[]
    projectsCount: number
    photo?: string
    user_id?: string
}

const statusMap: Record<string, { label: string; color: string; background: string }> = {
    online: { label: "Онлайн", color: "#48C166", background: "rgba(232, 250, 240, 1)" },
    recent: { label: "Был(а) недавно", color: "#F5A400", background: "rgba(253, 244, 230, 1)" },
    inactive: { label: "Неактивен", color: "#789CB0", background: "rgba(246, 245, 249, 1)" },
}

// Backend exposes last_activity (time of last login / token refresh, ISO, no tz).
// online: within 5 min, recent: within 24h, otherwise inactive.
const ONLINE_THRESHOLD_MS = 5 * 60 * 1000
const RECENT_THRESHOLD_MS = 24 * 60 * 60 * 1000

const getStatusFromActivity = (lastActivity?: string | null): string => {
    if (!lastActivity) {
        return 'inactive'
    }

    // last_activity has no timezone suffix; treat it as UTC.
    const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(lastActivity) ? lastActivity : `${lastActivity}Z`
    const activityTime = new Date(normalized).getTime()

    if (Number.isNaN(activityTime)) {
        return 'inactive'
    }

    const diff = Date.now() - activityTime

    if (diff <= ONLINE_THRESHOLD_MS) {
        return 'online'
    }

    if (diff <= RECENT_THRESHOLD_MS) {
        return 'recent'
    }

    return 'inactive'
}

// Fallback for projects whose color isn't yet known (project list still loading).
const DEFAULT_PROJECT_COLOR = '#B1AEC7'

const roleOptions = [
    { value: 'OWNER', label: '\u0420\u0443\u043a\u043e\u0432\u043e\u0434\u0438\u0442\u0435\u043b\u044c \u043e\u0442\u0434\u0435\u043b\u0430' },
    { value: 'ADMIN', label: '\u0410\u0434\u043c\u0438\u043d' },
    { value: 'SA', label: '\u0421\u0410' },
    { value: 'MEMBER', label: '\u041f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u0442\u0435\u043b\u044c' },
]

const roleAliases: Record<string, string> = {
    USER: 'MEMBER',
}

const normalizeRole = (role: string) => roleAliases[role] || role

const getRoleLabel = (role: string) => {
    const normalizedRole = normalizeRole(role)
    return roleOptions.find((option) => option.value === normalizedRole)?.label || role
}

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
)


export default function Participants(){
    const navigate = useNavigate()
    const location = useLocation()
    const { workspaceId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const targetUserId = searchParams.get('user')
    const searchQuery = searchParams.get('query') || ''
    const { workspace } = useWorkspace()
    const { user } = useUser()
    const [participants, setParticipants] = useState<Participant[]>([])
    const [projectColors, setProjectColors] = useState<Record<number, string>>({})
    const [search, setSearch] = useState(searchQuery)
    const [selectedIds, setSelectedIds] = useState<Array<number | string>>([])
    const [roleModal, setRoleModal] = useState<{ participant: Participant; newRole: string } | null>(null)
    const [cancelModal, setCancelModal] = useState<Participant | null>(null)
    const [deleteModal, setDeleteModal] = useState<Participant | null>(null)
    const [deletingMember, setDeletingMember] = useState(false)
    const [renameModal, setRenameModal] = useState<Participant | null>(null)
    const [renameValue, setRenameValue] = useState('')
    const [renamingMember, setRenamingMember] = useState(false)
    const [removeProjectModal, setRemoveProjectModal] = useState<{ participant: Participant; project: ParticipantProject } | null>(null)
    const [removingProject, setRemovingProject] = useState(false)
    const [addMemberModal, setAddMemberModal] = useState(false)
    const [updatingRole, setUpdatingRole] = useState(false)
    const [roleError, setRoleError] = useState<string | null>(null)
    const [cancellingInvitation, setCancellingInvitation] = useState(false)
    const [resendingInvitationId, setResendingInvitationId] = useState<number | string | null>(null)
    const [invitationNotification, setInvitationNotification] = useState<string | null>(null)

    useEffect(() => {
        if (!invitationNotification) return
        const timeoutId = setTimeout(() => setInvitationNotification(null), 4000)
        return () => clearTimeout(timeoutId)
    }, [invitationNotification])

    useEffect(() => {
        if (!targetUserId) {
            setSearch(searchQuery)
            return
        }

        const targetParticipant = participants.find((participant) => (
            matchesParticipantId(participant, targetUserId)
        ))
        setSearch(targetParticipant
            ? searchQuery || getParticipantSearchLabel(targetParticipant)
            : searchQuery)
    }, [participants, searchQuery, targetUserId])

    // Switching the workspace (e.g. from the sidebar) only updates the context.
    // Keep the URL in sync so this page shows members of the selected workspace.
    useEffect(() => {
        if (!workspace?.id) return
        if (String(workspace.id) === workspaceId) return

        // A deep link from search may target another workspace: switching to it
        // must keep the ?user/?query context, otherwise the page falls back to
        // the full member list and the search result is lost.
        navigate(`/participants/${workspace.id}${location.search}`, { replace: true })
    }, [workspace?.id, workspaceId, navigate, location.search])

    useEffect(() => {
        if (!workspaceId) return

        const fetchMembers = async () => {
            try {
                const [membersData, photosData] = await Promise.all([
                    WorkspaceMembersService.getMembers(Number(workspaceId)),
                    WorkspaceMembersService.getMemberPhotos(Number(workspaceId)).catch(() => null),
                ])
                const members: any[] = extractList(membersData)
                const photoItems: any[] = extractList(photosData)
                const photoMap = new Map<string, string>()
                photoItems.forEach((p: any) => {
                    if (p.user_id && p.photo_data_url) {
                        photoMap.set(p.user_id, p.photo_data_url)
                    }
                })

                setParticipants(members.map((m) => {
                    const user = m.user || {}
                    const firstName = user.first_name || m.first_name || ''
                    const lastName = user.last_name || m.last_name || ''
                    const fullName = `${firstName} ${lastName}`.trim()
                    const userId = m.user_id || user.user_id || user.id || ''
                    const projects: ParticipantProject[] = Array.isArray(m.projects)
                        ? m.projects.map((p: any) => ({
                            id: p.id,
                            name: p.name,
                            projectMemberId: p.project_member_id ?? p.member_id,
                        }))
                        : []
                    return {
                        id: m.id ?? userId,
                        name: fullName || user.email || m.email || userId || '',
                        email: user.email || m.email || '',
                        role: m.role_in_workspace,
                        status: getStatusFromActivity(m.last_activity),
                        pending: Boolean(m.is_pending_invitation),
                        // Pending invitations have no numeric member id; the backend
                        // identifies them by the invited user_id.
                        invitationId: m.invitation_id ?? m.invitation?.id ?? userId,
                        projects,
                        projectsCount: typeof m.projects_count === 'number' ? m.projects_count : projects.length,
                        photo: photoMap.get(userId) || '',
                        user_id: userId,
                    }
                }))
            } catch (e) {
                console.error('Failed to fetch members:', e)
            }
        }

        fetchMembers()
    }, [workspaceId])

    // The members endpoint returns projects as {id, name} only — pull colors
    // from the workspace project list and match by id for the icon tint.
    useEffect(() => {
        if (!workspaceId) return

        const fetchProjectColors = async () => {
            try {
                const data = await ProjectService.list()
                const list: any[] = data?.projects || data || []
                const colorMap: Record<number, string> = {}
                list.forEach((project) => {
                    if (project?.id != null && project.color) {
                        colorMap[Number(project.id)] = project.color
                    }
                })
                setProjectColors(colorMap)
            } catch (e) {
                console.error('Failed to fetch project colors:', e)
            }
        }

        fetchProjectColors()
    }, [workspaceId])

    const isAllSelected = participants.length > 0 && selectedIds.length === participants.length
    const isSomeSelected = selectedIds.length > 0 && !isAllSelected

    const handleSelectAll = () => {
        if (selectedIds.length > 0) {
            setSelectedIds([])
        } else {
            setSelectedIds(participants.map((p: Participant) => p.id))
        }
    }

    const handleToggleItem = (id: number | string) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id]
        )
    }

    const handleRoleClick = (participant: Participant, newRole: string) => {
        if (!canChangeMemberRole(participant, newRole, user)) {
            return
        }

        if (newRole !== normalizeRole(participant.role)) {
            setRoleError(null)
            setRoleModal({ participant, newRole })
        }
    }

    const closeRoleModal = () => {
        if (updatingRole) {
            return
        }

        setRoleError(null)
        setRoleModal(null)
    }

    const handleConfirmRole = async () => {
        if (!roleModal || updatingRole) {
            return
        }

        if (!canChangeMemberRole(roleModal.participant, roleModal.newRole, user)) {
            setRoleError('\u041d\u0435\u0434\u043e\u0441\u0442\u0430\u0442\u043e\u0447\u043d\u043e \u043f\u0440\u0430\u0432 \u0434\u043b\u044f \u0438\u0437\u043c\u0435\u043d\u0435\u043d\u0438\u044f \u044d\u0442\u043e\u0439 \u0440\u043e\u043b\u0438')
            return
        }

        setUpdatingRole(true)
        setRoleError(null)

        try {
            const updatedMember = await WorkspaceMembersService.updateMember(roleModal.participant.id, {
                role_in_workspace: roleModal.newRole,
            })

            setParticipants((prev) => prev.map((participant) => {
                if (participant.id !== roleModal.participant.id) {
                    return participant
                }

                return {
                    ...participant,
                    role: updatedMember?.role_in_workspace || roleModal.newRole,
                }
            }))

            setRoleModal(null)
        } catch (e: any) {
            console.error('Failed to update workspace member role:', e)
            setRoleError(
                e?.response?.data?.detail ||
                e?.data?.detail ||
                '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0438\u0437\u043c\u0435\u043d\u0438\u0442\u044c \u0440\u043e\u043b\u044c'
            )
        } finally {
            setUpdatingRole(false)
        }
    }

    const handleConfirmCancel = async () => {
        if (!cancelModal || cancellingInvitation) {
            return
        }

        const workspaceIdNum = Number(workspaceId || workspace?.id)
        const invitationId = cancelModal.invitationId ?? cancelModal.id

        if (!workspaceIdNum || invitationId == null) {
            setCancelModal(null)
            return
        }

        setCancellingInvitation(true)

        try {
            await WorkspaceMembersService.cancelInvitation(workspaceIdNum, invitationId)
            setParticipants((prev) => prev.filter((participant) => participant.id !== cancelModal.id))
            setCancelModal(null)
        } catch (e) {
            console.error('Failed to cancel workspace invitation:', e)
        } finally {
            setCancellingInvitation(false)
        }
    }

    const handleResendInvitation = async (participant: Participant) => {
        if (resendingInvitationId === participant.id) {
            return
        }

        const workspaceIdNum = Number(workspaceId || workspace?.id)
        const invitationId = participant.invitationId ?? participant.id

        if (!workspaceIdNum || invitationId == null) {
            console.warn('Cannot resend invitation: missing workspaceId or invitationId', { workspaceIdNum, invitationId })
            return
        }

        setResendingInvitationId(participant.id)

        try {
            await WorkspaceMembersService.resendInvitation(workspaceIdNum, invitationId)
            setInvitationNotification(`Приглашение для ${participant.email || participant.name} отправлено повторно`)
        } catch (e) {
            console.error('Failed to resend workspace invitation:', e)
        } finally {
            setResendingInvitationId(null)
        }
    }

    const openDeleteModal = (participant: Participant) => {
        setDeleteModal(participant)
    }

    const handleConfirmDelete = async () => {
        if (!deleteModal || deletingMember) {
            return
        }

        setDeletingMember(true)

        try {
            await WorkspaceMembersService.deleteMember(deleteModal.id)
            setParticipants((prev) => prev.filter((participant) => participant.id !== deleteModal.id))
            setDeleteModal(null)
        } catch (e) {
            console.error('Failed to delete workspace member:', e)
        } finally {
            setDeletingMember(false)
        }
    }

    const openRenameModal = (participant: Participant) => {
        setRenameValue(participant.name)
        setRenameModal(participant)
    }

    const handleConfirmRename = async () => {
        if (!renameModal || renamingMember) {
            return
        }

        const trimmed = renameValue.trim()
        if (!trimmed) {
            return
        }

        setRenamingMember(true)

        const [firstName, ...rest] = trimmed.split(' ')
        const lastName = rest.join(' ')

        try {
            await WorkspaceMembersService.updateMember(renameModal.id, {
                first_name: firstName,
                last_name: lastName,
            })
            setParticipants((prev) => prev.map((participant) => (
                participant.id === renameModal.id ? { ...participant, name: trimmed } : participant
            )))
            setRenameModal(null)
        } catch (e) {
            console.error('Failed to rename workspace member:', e)
        } finally {
            setRenamingMember(false)
        }
    }

    const openRemoveProjectModal = (participant: Participant, project: ParticipantProject) => {
        setRemoveProjectModal({ participant, project })
    }

    const handleConfirmRemoveProject = async () => {
        if (!removeProjectModal || removingProject) {
            return
        }

        const { participant, project } = removeProjectModal

        setRemovingProject(true)

        try {
            if (!participant.user_id) {
                console.warn('No user_id found to remove from project:', { participantId: participant.id, projectId: project.id })
                setRemoveProjectModal(null)
                return
            }

            await WorkspaceMembersService.removeProjectMember(Number(project.id), participant.user_id)
            setParticipants((prev) => prev.map((item) => {
                if (item.id !== participant.id) {
                    return item
                }

                const projects = item.projects.filter((p) => p.id !== project.id)
                return { ...item, projects, projectsCount: Math.max(0, item.projectsCount - 1) }
            }))
            setRemoveProjectModal(null)
        } catch (e) {
            console.error('Failed to remove member from project:', e)
        } finally {
            setRemovingProject(false)
        }
    }


    const filteredParticipants = filterParticipants(participants, search, targetUserId)
    const participantPagination = useListPagination(filteredParticipants)

    const handleSearchChange = (value: string) => {
        setSearch(value)

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('user')
        nextParams.delete('page')

        if (value.trim()) {
            nextParams.set('query', value)
        } else {
            nextParams.delete('query')
        }

        setSearchParams(nextParams, { replace: true })
    }

    const handleMembersAdded = (createdMembers: any[]) => {
        const newParticipants = createdMembers.map((member: any) => {
            const selectedUser = member.__selectedUser || {}
            const firstName = member.first_name || selectedUser.first_name || ''
            const lastName = member.last_name || selectedUser.last_name || ''
            const fullName = `${firstName} ${lastName}`.trim()
            const userId = member.user_id || selectedUser.id

            return {
                id: member.id,
                name: fullName || member.email || selectedUser.email || userId,
                email: member.email || selectedUser.email || '',
                role: member.role_in_workspace || 'MEMBER',
                status: getStatusFromActivity(member.last_activity),
                pending: Boolean(member.is_pending_invitation),
                invitationId: member.invitation_id ?? member.invitation?.id ?? member.id,
                projects: Array.isArray(member.projects)
                    ? member.projects.map((p: any) => ({
                        id: p.id,
                        name: p.name,
                        projectMemberId: p.project_member_id ?? p.member_id,
                    }))
                    : [],
                projectsCount: typeof member.projects_count === 'number' ? member.projects_count : 0,
                photo: '',
                user_id: userId,
            }
        })

        setParticipants((prev) => [...prev, ...newParticipants])
    }

    return (
        <>
            <Header />

            <div className={styles.participants}>
                <div className={styles.participants__topRow}>
                    <nav className={styles.participants__breadcrumbs} aria-label="Хлебные крошки">
                        <button
                            type="button"
                            className={styles.participants__breadcrumbLink}
                            title={workspace?.name || 'Рабочее пространство'}
                            onClick={() => navigate('/workspaces')}
                        >
                            {workspace?.name || 'Рабочее пространство'}
                        </button>
                        <span className={styles.participants__breadcrumbSeparator}>/</span>
                        <span className={styles.participants__breadcrumbCurrent}>Участники</span>
                    </nav>
                    <button
                        type="button"
                        className={styles.participants__addBtn}
                        onClick={() => setAddMemberModal(true)}
                    >
                        <span className={styles.participants__addBtnIcon}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7 0C7.55228 0 8 0.447715 8 1V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V1C6 0.447715 6.44772 0 7 0Z" fill="#453CFB" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M0 7C0 6.44771 0.447715 6 1 6L13 6C13.5523 6 14 6.44772 14 7C14 7.55229 13.5523 8 13 8H1C0.447715 8 0 7.55228 0 7Z" fill="#453CFB" />
                            </svg>
                        </span>
                        <span>Добавить участника</span>
                    </button>
                </div>

                <div className={styles.participants__toolbar}>
                    <div className={styles.participants__search}>
                        <SearchIcon />
                        <input
                            className={styles.participants__searchInput}
                            placeholder="Введіть ім'я або пошту"
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>
                </div>

                <div className={styles.participants__list}>
                    <div className={styles.participants__header}>
                        <div className={styles.participants__headerCheckbox}>
                            <div
                                className={`${styles.participants__checkbox} ${isAllSelected ? styles.participants__checkboxActive : ''} ${isSomeSelected ? styles.participants__checkboxPartial : ''}`}
                                onClick={handleSelectAll}
                            >
                                {isAllSelected && <CheckIcon />}
                                {isSomeSelected && <div className={styles.participants__checkboxDash} />}
                            </div>
                        </div>
                        <div className={styles.participants__headerName}>ФИО</div>
                        <div className={styles.participants__headerEmail}>Email</div>
                        <div className={styles.participants__headerRole}>Роль</div>
                        <div className={styles.participants__headerProjects}>Проекты</div>
                        <div className={styles.participants__headerActivity}>Последняя активность</div>
                    </div>

                    <div className={styles.participants__body}>
                        {participantPagination.paginatedItems.map((participant) => {
                            const status = statusMap[participant.status]
                            const isSelected = selectedIds.includes(participant.id)
                            const availableRoleOptions = roleOptions.filter((role) => (
                                canChangeMemberRole(participant, role.value, user)
                            ))
                            const canEditRole = availableRoleOptions.some((role) => (
                                role.value !== normalizeRole(participant.role)
                            ))
                            const displayName = participant.pending
                                ? (participant.name.length > 20 ? participant.name.slice(0, 20) + '...' : participant.name)
                                : participant.name

                            return (
                                <div className={`${styles.participants__row} ${isSelected ? styles.participants__rowSelected : ''}`} key={participant.id}>
                                    <div className={styles.participants__rowCheckbox}>
                                        <div
                                            className={`${styles.participants__checkbox} ${isSelected ? styles.participants__checkboxActive : ''}`}
                                            onClick={() => handleToggleItem(participant.id)}
                                        >
                                            {isSelected && <CheckIcon />}
                                        </div>
                                    </div>
                                    <div className={styles.participants__rowName}>
                                        {participant.photo ? (
                                            <img
                                                className={styles.participants__avatar}
                                                src={participant.photo}
                                                alt={participant.name}
                                            />
                                        ) : (
                                            <div className={styles.participants__avatarPlaceholder}>
                                                {participant.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                                            </div>
                                        )}
                                        <span className={styles.participants__name}>{displayName}</span>
                                        {participant.pending && (
                                            <div className={styles.participants__pending}>
                                                <PendingIcon />
                                                <span className={styles.participants__pendingText}>В ожидании</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.participants__rowEmail}>
                                        <span className={styles.participants__emailText}>{participant.email}</span>
                                    </div>
                                    <div className={styles.participants__rowRole}>
                                        {canEditRole ? (
                                            <DropDown
                                                closeDropDownByPick
                                                btn={
                                                    <div className={styles.participants__roleBtn}>
                                                        <span className={styles.participants__roleText}>{getRoleLabel(participant.role)}</span>
                                                        <span className={styles.participants__roleChevron}><ChevronDownIcon /></span>
                                                    </div>
                                                }
                                            >
                                                <div className={styles.participants__roleDropdown}>
                                                    {availableRoleOptions.map((role) => (
                                                        <div
                                                            className={styles.participants__roleDropdownItem}
                                                            key={role.value}
                                                            onClick={() => handleRoleClick(participant, role.value)}
                                                        >
                                                            <span>{role.label}</span>
                                                            {normalizeRole(participant.role) === role.value && (
                                                                <span className={styles.participants__roleDropdownDot} />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </DropDown>
                                        ) : (
                                            <div className={styles.participants__roleBtnStatic}>
                                                <span className={styles.participants__roleText}>{getRoleLabel(participant.role)}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.participants__rowProjects}>
                                        {participant.projectsCount > 0 ? (
                                            <DropDown
                                                btn={
                                                    <div className={styles.participants__projectsBtn}>
                                                        <span className={styles.participants__projectsText}>
                                                            Участвует в ({participant.projectsCount})
                                                        </span>
                                                        <span className={styles.participants__projectsChevron}><ChevronDownIcon /></span>
                                                    </div>
                                                }
                                            >
                                                <div className={styles.participants__projectsDropdown}>
                                                    {participant.projects.length > 0 ? (
                                                        participant.projects.map((project) => (
                                                            <div className={styles.participants__projectsDropdownItem} key={project.id}>
                                                                <span className={styles.participants__projectsDropdownIcon}>
                                                                    <span
                                                                        className={styles.participants__projectsDropdownMarker}
                                                                        style={{ borderColor: projectColors[project.id] || DEFAULT_PROJECT_COLOR }}
                                                                    />
                                                                </span>
                                                                <span className={styles.participants__projectsDropdownName}>{project.name}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.participants__projectsDropdownRemove}
                                                                    onClick={() => openRemoveProjectModal(participant, project)}
                                                                    aria-label="Удалить из проекта"
                                                                >
                                                                    <TrashIcon />
                                                                </button>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className={styles.participants__projectsDropdownEmpty}>
                                                            Нет проектов
                                                        </div>
                                                    )}
                                                </div>
                                            </DropDown>
                                        ) : (
                                            <span className={styles.participants__projectsEmpty}>—</span>
                                        )}
                                    </div>
                                    <div className={styles.participants__rowActivity}>
                                        <div className={styles.participants__status} style={{ background: status.background }}>
                                            <span className={styles.participants__statusDot} style={{ background: status.color }} />
                                            <span className={styles.participants__statusText}>{status.label}</span>
                                        </div>
                                        <div className={styles.participants__actionsWrap}>
                                            <DropDown
                                                closeDropDownByPick
                                                position="right"
                                                btn={
                                                    <div className={styles.participants__actionsBtn}>
                                                        <DotsVerticalIcon />
                                                    </div>
                                                }
                                            >
                                                <div className={styles.participants__actionsDropdown}>
                                                    {participant.pending ? (
                                                        <>
                                                            <div
                                                                className={styles.participants__actionsDropdownItem}
                                                                onClick={() => handleResendInvitation(participant)}
                                                            >
                                                                <ResendIcon />
                                                                <span>Отправить повторно</span>
                                                            </div>
                                                            <div
                                                                className={`${styles.participants__actionsDropdownItem} ${styles.participants__actionsDropdownItemDanger}`}
                                                                onClick={() => setCancelModal(participant)}
                                                            >
                                                                <CancelSendIcon />
                                                                <span>Отменить приглашение</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div
                                                                className={styles.participants__actionsDropdownItem}
                                                                onClick={() => openRenameModal(participant)}
                                                            >
                                                                <EditIcon />
                                                                <span>Переименовать</span>
                                                            </div>
                                                            <div
                                                                className={`${styles.participants__actionsDropdownItem} ${styles.participants__actionsDropdownItemDanger}`}
                                                                onClick={() => openDeleteModal(participant)}
                                                            >
                                                                <TrashIcon />
                                                                <span>Удалить</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </DropDown>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        {participants.length > 0 && filteredParticipants.length === 0 && (
                            <div className={styles.participants__empty}>
                                {targetUserId
                                    ? 'Участник не найден в этом пространстве.'
                                    : 'Участники не найдены.'}
                                {(targetUserId || search) && (
                                    <button
                                        type="button"
                                        className={styles.participants__emptyReset}
                                        onClick={() => handleSearchChange('')}
                                    >
                                        Показать всех участников
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <Pagination
                    page={participantPagination.page}
                    totalItems={participantPagination.totalItems}
                    onPageChange={participantPagination.setPage}
                />

                <Modal isOpen={!!roleModal} onRequestClose={closeRoleModal} width={446}>
                    <div className={styles.participants__modal}>
                        <div className={styles.participants__modalClose} onClick={closeRoleModal}>
                            <CloseIcon />
                        </div>
                        <div className={styles.participants__modalContent}>
                            <h3 className={styles.participants__modalTitle}>Изменить роль участника?</h3>
                            <p className={styles.participants__modalText}>
                                Вы хотите изменить роль {roleModal?.participant.name} с «{getRoleLabel(roleModal?.participant.role || '')}» на «{getRoleLabel(roleModal?.newRole || '')}»
                            </p>
                        </div>
                        {roleError && (
                            <div className={styles.participants__modalError}>
                                {roleError}
                            </div>
                        )}
                        <div className={styles.participants__modalActions}>
                            <button className={styles.participants__modalBtnCancel} onClick={closeRoleModal} disabled={updatingRole}>
                                Отменить
                            </button>
                            <button
                                className={`${styles.participants__modalBtnConfirm} ${updatingRole ? styles.participants__modalBtnDisabled : ''}`}
                                onClick={handleConfirmRole}
                                disabled={updatingRole}
                            >
                                {updatingRole ? 'Изменение...' : 'Изменить'}
                            </button>
                        </div>
                    </div>
                </Modal>

                <Modal isOpen={!!cancelModal} onRequestClose={() => !cancellingInvitation && setCancelModal(null)} width={446}>
                    <div className={styles.participants__modal}>
                        <div className={styles.participants__modalClose} onClick={() => !cancellingInvitation && setCancelModal(null)}>
                            <CloseIcon />
                        </div>
                        <div className={styles.participants__modalContent}>
                            <h3 className={styles.participants__modalTitle}>Отменить приглашение участника?</h3>
                            <p className={styles.participants__modalText}>
                                Участник покинет это пространство и у него не будет доступа ко всем материалам
                            </p>
                        </div>
                        <div className={styles.participants__modalActions}>
                            <button className={styles.participants__modalBtnCancel} onClick={() => setCancelModal(null)} disabled={cancellingInvitation}>
                                Отменить
                            </button>
                            <button className={styles.participants__modalBtnDanger} onClick={handleConfirmCancel} disabled={cancellingInvitation}>
                                {cancellingInvitation ? 'Удаление...' : 'Удалить'}
                            </button>
                        </div>
                    </div>
                </Modal>


                <Modal isOpen={!!deleteModal} onRequestClose={() => !deletingMember && setDeleteModal(null)} width={446}>
                    <div className={styles.participants__modal}>
                        <div className={styles.participants__modalClose} onClick={() => !deletingMember && setDeleteModal(null)}>
                            <CloseIcon />
                        </div>
                        <div className={styles.participants__modalContent}>
                            <h3 className={styles.participants__modalTitle}>Удалить участника?</h3>
                            <p className={styles.participants__modalText}>
                                Вы уверены, что хотите удалить {deleteModal?.name}? Участник покинет это пространство и потеряет доступ ко всем материалам.
                            </p>
                        </div>
                        <div className={styles.participants__modalActions}>
                            <button className={styles.participants__modalBtnCancel} onClick={() => setDeleteModal(null)} disabled={deletingMember}>
                                Отменить
                            </button>
                            <button className={styles.participants__modalBtnDanger} onClick={handleConfirmDelete} disabled={deletingMember}>
                                {deletingMember ? 'Удаление...' : 'Удалить'}
                            </button>
                        </div>
                    </div>
                </Modal>

                <Modal isOpen={!!removeProjectModal} onRequestClose={() => !removingProject && setRemoveProjectModal(null)} width={446}>
                    <div className={styles.participants__modal}>
                        <div className={styles.participants__modalClose} onClick={() => !removingProject && setRemoveProjectModal(null)}>
                            <CloseIcon />
                        </div>
                        <div className={styles.participants__modalContent}>
                            <h3 className={styles.participants__modalTitle}>Удалить из проекта?</h3>
                            <p className={styles.participants__modalText}>
                                Вы уверены, что хотите удалить {removeProjectModal?.participant.name} из проекта «{removeProjectModal?.project.name}»?
                            </p>
                        </div>
                        <div className={styles.participants__modalActions}>
                            <button className={styles.participants__modalBtnCancel} onClick={() => setRemoveProjectModal(null)} disabled={removingProject}>
                                Отменить
                            </button>
                            <button className={styles.participants__modalBtnDanger} onClick={handleConfirmRemoveProject} disabled={removingProject}>
                                {removingProject ? 'Удаление...' : 'Удалить'}
                            </button>
                        </div>
                    </div>
                </Modal>

                <Modal isOpen={!!renameModal} onRequestClose={() => !renamingMember && setRenameModal(null)} width={446}>
                    <div className={styles.participants__modal}>
                        <div className={styles.participants__modalClose} onClick={() => !renamingMember && setRenameModal(null)}>
                            <CloseIcon />
                        </div>
                        <div className={styles.participants__modalContent}>
                            <h3 className={styles.participants__modalTitle}>Переименовать участника</h3>
                            <input
                                className={styles.participants__renameInput}
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                placeholder="Имя участника"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') void handleConfirmRename()
                                }}
                            />
                        </div>
                        <div className={styles.participants__modalActions}>
                            <button className={styles.participants__modalBtnCancel} onClick={() => setRenameModal(null)} disabled={renamingMember}>
                                Отменить
                            </button>
                            <button
                                className={`${styles.participants__modalBtnConfirm} ${renamingMember || !renameValue.trim() ? styles.participants__modalBtnDisabled : ''}`}
                                onClick={handleConfirmRename}
                                disabled={renamingMember || !renameValue.trim()}
                            >
                                {renamingMember ? 'Сохранение...' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </Modal>


            </div>

            <AddMemberModal
                isOpen={addMemberModal}
                onClose={() => setAddMemberModal(false)}
                onMembersAdded={handleMembersAdded}
            />

            {invitationNotification && (
                <div className={styles.participants__toast}>
                    {invitationNotification}
                </div>
            )}
        </>
    )
}
